import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Badge, Drawer, Box, Typography, IconButton, Chip, TextField,
  Button, CircularProgress, Tooltip, MenuItem,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { AuthContext } from '../../context/logeduser';
import { useMyTickets, useCreateTicket, useHrOptions } from '../../hooks/useSupport';
import SupportChatThread from '../common/SupportChatThread';
import DraggableFab from '../common/DraggableFab';

const ACCENT = '#7c5cff';

const CATEGORIES = [
  { value: 'PAYROLL', label: 'Payroll & Salary' },
  { value: 'LEAVE', label: 'Leave & Attendance' },
  { value: 'DOCUMENTS', label: 'Documents & Form16' },
  { value: 'IT', label: 'IT & Access' },
  { value: 'GENERAL', label: 'Policy & General' },
];

const STATUS_META = {
  OPEN: { label: 'Open', fg: '#b8631f', bg: '#fbf0e4' },
  ANSWERED: { label: 'Answered', fg: '#0f7a72', bg: '#e7f5f3' },
  AWAITING_EMPLOYEE: { label: 'Awaiting you', fg: '#a23d68', bg: '#f8e9ef' },
  RESOLVED: { label: 'Resolved', fg: '#1f8a5f', bg: '#e8f5ee' },
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, fg: '#656b85', bg: '#f1f2f6' };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700, fontSize: '.68rem', height: 22 }}
    />
  );
};

const categoryLabel = (value) => CATEGORIES.find((c) => c.value === value)?.label || value;

const timeAgo = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
};

function TicketList({ tickets, isLoading, onOpen, onNew }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Button
          fullWidth variant="contained" startIcon={<AddIcon />} onClick={onNew}
          sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 700, borderRadius: '12px', '&:hover': { bgcolor: '#2559d6' } }}
        >
          New query
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
        )}
        {!isLoading && tickets.length === 0 && (
          <Box sx={{ textAlign: 'center', color: '#8a92ac', mt: 6, px: 2 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 34, mb: 1, opacity: .5 }} />
            <Typography sx={{ fontSize: '.85rem' }}>
              No queries yet. Ask HR about payroll, leave, documents, or anything else.
            </Typography>
          </Box>
        )}
        {tickets.map((t) => (
          <Box
            key={t.id}
            onClick={() => onOpen(t.id)}
            sx={{
              p: 1.5, mb: 1.25, borderRadius: '14px', border: '1px solid #eef0f4',
              cursor: 'pointer', bgcolor: t.employee_unread ? '#f3f7ff' : '#fff',
              '&:hover': { borderColor: ACCENT },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: .5 }}>
              <Typography sx={{ fontSize: '.72rem', fontWeight: 700, color: '#656b85' }}>
                {categoryLabel(t.category)}
              </Typography>
              <StatusChip status={t.status} />
            </Box>
            <Typography sx={{
              fontSize: '.82rem', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {t.last_message}
            </Typography>
            {t.assigned_hr_name && (
              <Typography sx={{ fontSize: '.68rem', color: '#656b85', mt: .5 }}>Assigned to {t.assigned_hr_name}</Typography>
            )}
            <Typography sx={{ fontSize: '.68rem', color: '#9298b2', mt: .5 }}>
              #{t.id} &middot; {timeAgo(t.updated_at)} ago
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function NewTicketForm({ onCreated, onCancel }) {
  const [category, setCategory] = useState('PAYROLL');
  const [message, setMessage] = useState('');
  const [selectedHrId, setSelectedHrId] = useState('');
  const createTicket = useCreateTicket();
  const { data: hrOptions = [], isLoading: hrOptionsLoading, isError: hrOptionsError } = useHrOptions();

  const submit = async () => {
    if (!message.trim() || !selectedHrId) return;
    const ticket = await createTicket.mutateAsync({ category, message: message.trim(), selectedHrId: Number(selectedHrId) });
    onCreated(ticket.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      <Typography sx={{ fontSize: '.78rem', fontWeight: 700, color: '#656b85', mb: 1 }}>
        WHAT'S THIS ABOUT?
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {CATEGORIES.map((c) => (
          <Chip
            key={c.value}
            label={c.label}
            onClick={() => setCategory(c.value)}
            sx={{
              fontSize: '.76rem',
              bgcolor: category === c.value ? ACCENT : '#f6f7fc',
              color: category === c.value ? '#fff' : '#656b85',
              fontWeight: category === c.value ? 700 : 500,
              border: '1px solid', borderColor: category === c.value ? ACCENT : '#eef0f4',
            }}
          />
        ))}
      </Box>
      <Typography sx={{ fontSize: '.78rem', fontWeight: 700, color: '#656b85', mb: 1 }}>
        CHOOSE YOUR HR
      </Typography>
      <TextField
        select fullWidth size="small" label="HR" value={selectedHrId}
        onChange={(event) => setSelectedHrId(event.target.value)}
        disabled={hrOptionsLoading || hrOptionsError}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      >
        {hrOptions.map((hr) => (
          <MenuItem key={hr.id} value={hr.id}>
            {hr.name}{hr.branch ? ` - ${hr.branch}` : (hr.location ? ` - ${hr.location}` : '')}
          </MenuItem>
        ))}
      </TextField>
      {hrOptionsLoading && (
        <Typography sx={{ color: '#656b85', fontSize: '.75rem', mb: 1 }}>Loading available HRs...</Typography>
      )}
      {hrOptionsError && (
        <Typography sx={{ color: '#c0392b', fontSize: '.75rem', mb: 1 }}>Could not load the HR list. Please try again.</Typography>
      )}
      <TextField
        multiline minRows={5} placeholder="Describe your query…" value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', alignItems: 'flex-start' } }}
      />
      {createTicket.isError && (
        <Typography sx={{ color: '#c0392b', fontSize: '.75rem', mt: 1 }}>
          {createTicket.error?.response?.data?.error || 'Could not send — please try again.'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', color: '#656b85' }}>Cancel</Button>
        <Button
          fullWidth variant="contained" onClick={submit} disabled={!message.trim() || !selectedHrId || createTicket.isPending}
          sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 700, borderRadius: '12px', '&:hover': { bgcolor: '#2559d6' } }}
        >
          {createTicket.isPending ? 'Sending…' : 'Send to HR'}
        </Button>
      </Box>
    </Box>
  );
}

function ThreadView({ ticketId, onBack }) {
  return (
    <SupportChatThread
      ticketId={ticketId}
      viewerIsHr={false}
      accent={ACCENT}
      composerPlaceholder="Message HR…"
      renderHeader={(ticket) => (
        <Box sx={{ p: 1.5, borderBottom: '1px solid #eef0f4', display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={onBack}><ArrowBackIcon fontSize="small" /></IconButton>
          {ticket && (
            <>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '.82rem', fontWeight: 700 }}>{categoryLabel(ticket.category)}</Typography>
                <Typography sx={{ fontSize: '.68rem', color: '#9298b2' }}>Ticket #{ticket.id}</Typography>
                {ticket.assigned_hr_name && (
                  <Typography sx={{ fontSize: '.68rem', color: '#656b85' }}>Assigned to {ticket.assigned_hr_name}</Typography>
                )}
              </Box>
              <StatusChip status={ticket.status} />
            </>
          )}
        </Box>
      )}
    />
  );
}

export default function AskHRWidget() {
  const { authData } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'new' | 'thread'
  const [activeTicketId, setActiveTicketId] = useState(null);

  // Only render for a logged-in employee session (never for admin/HR — Sd.js
  // side never mounts this component anyway, but this guards direct reuse).
  const isEmployeeSession = !!authData && !authData.admin;

  const { data: tickets = [], isLoading } = useMyTickets({ enabled: isEmployeeSession });

  const unreadCount = useMemo(() => tickets.filter((t) => t.employee_unread).length, [tickets]);

  useEffect(() => {
    if (open && tickets.length === 0 && !isLoading) setView('new');
  }, [open, tickets.length, isLoading]);

  if (!isEmployeeSession) return null;

  const openThread = (id) => { setActiveTicketId(id); setView('thread'); };

  return (
    <>
      <Tooltip title="Ask HR — drag to move" placement="left">
        <DraggableFab
          storageKey="askhr:fabPos"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed', bottom: { xs: 16, md: 24 }, right: { xs: 16, md: 24 }, zIndex: 1200,
            background: `linear-gradient(135deg, ${ACCENT} 0%, #1948c9 100%)`,
            color: '#fff', boxShadow: '0 8px 24px rgba(47,107,255,.35)',
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <ChatBubbleOutlineIcon />
          </Badge>
        </DraggableFab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #eef0f4', display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>Ask HR</Typography>
            <IconButton onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {view === 'list' && (
              <TicketList
                tickets={tickets}
                isLoading={isLoading}
                onOpen={openThread}
                onNew={() => setView('new')}
              />
            )}
            {view === 'new' && (
              <NewTicketForm
                onCreated={openThread}
                onCancel={() => (tickets.length ? setView('list') : setOpen(false))}
              />
            )}
            {view === 'thread' && activeTicketId && (
              <ThreadView
                ticketId={activeTicketId}
                onBack={() => setView('list')}
              />
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
